import { SessionGuardrailResult } from "@financial-advisory/data-access/api-types";
import type { SessionGuardrailTree } from "@financial-advisory/data-access/api-types";

/**
 * Whether a guardrail let the message through.
 *
 * The API records the outcome as the root node's value, so "TRUE" means the
 * message satisfies the rule. "Yes" is accepted as well because earlier code
 * read the outcome that way, and sessions recorded then must still render.
 */
export const guardrailPassed = (
  tree: Pick<SessionGuardrailTree, "outcome">
): boolean => tree.outcome === "TRUE" || tree.outcome === "Yes";

/**
 * Checks if a guardrail result is an override-pass result (empty guardrail_trees).
 * Override-pass results are created when default messages have their guardrails
 * overridden to pass automatically (e.g. error responses, completion messages).
 */
const isOverridePassResult = (result: SessionGuardrailResult): boolean =>
  !result.guardrail_trees || result.guardrail_trees.length === 0;

/**
 * Filters guardrail results to only include those with output guardrails
 * (type "OUTPUT" or null for backwards compatibility).
 * Also includes override-pass results (empty guardrail_trees) where the
 * message role is "assistant", since these represent default messages that
 * had their output guardrails overridden to pass.
 */
export const filterOutputGuardrailResults = (
  allResults: SessionGuardrailResult[]
): SessionGuardrailResult[] =>
  allResults.filter(
    (result) =>
      result.guardrail_trees?.some(
        (tree) =>
          tree.guardrail_tree?.type === "OUTPUT" ||
          tree.guardrail_tree?.type === null
      ) ||
      (isOverridePassResult(result) && result.message?.role === "assistant")
  );

/**
 * Filters guardrail results to only include those with input guardrails (type "INPUT").
 * Also includes override-pass results (empty guardrail_trees) where the
 * message role is "user".
 */
export const filterInputGuardrailResults = (
  allResults: SessionGuardrailResult[]
): SessionGuardrailResult[] =>
  allResults.filter(
    (result) =>
      result.guardrail_trees?.some(
        (tree) => tree.guardrail_tree?.type === "INPUT"
      ) ||
      (isOverridePassResult(result) && result.message?.role === "user")
  );
